import React, { useEffect, useRef, useState } from 'react';

const ACCEPT = 'image/png,image/jpeg,image/gif';
const IMAGE_NAME = /\.(png|jpe?g|gif)$/i;
// Keep each request small enough that Paperclip + storage sync finishes.
const UPLOAD_BATCH = 6;

// The describer sees one picture filling most of the screen.
const MIN_LONG_EDGE = 600;
const MAX_BYTES = 1024 * 1024;

const qualityCache = new Map();

function loadDimensions(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = src;
  });
}

async function fileBytes(src) {
  const res = await fetch(src, { method: 'HEAD' });
  const length = res.ok && res.headers.get('content-length');
  return length ? Number(length) : null;
}

function isUploadableImage(file) {
  const type = file.type || '';
  if (!type) return IMAGE_NAME.test(file.name || '');
  return type === 'image/png' || type === 'image/jpeg' || type === 'image/gif' || type === 'image/jpg';
}

// Dropping onto <input type="file"> keeps only the first file, even with `multiple`.
// Read the file list ourselves, from a drop target that is not the input.
function filesFromDataTransfer(dt) {
  if (!dt) return [];
  const fromItems = [];
  if (dt.items) {
    Array.from(dt.items).forEach((item) => {
      if (item.kind !== 'file') return;
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    });
  }
  const fromList = dt.files ? Array.from(dt.files) : [];
  return fromItems.length >= fromList.length ? fromItems : fromList;
}

function dragHasFiles(dt) {
  return !!dt && Array.from(dt.types || []).includes('Files');
}

function pictureIssues(dimensions, bytes) {
  const issues = [];
  if (dimensions && Math.max(dimensions.width, dimensions.height) < MIN_LONG_EDGE) {
    issues.push(`Too small: ${dimensions.width}×${dimensions.height}px (needs ${MIN_LONG_EDGE}px+ on the long side)`);
  }
  if (bytes && bytes > MAX_BYTES) {
    issues.push(`Too large: ${(bytes / (1024 * 1024)).toFixed(1)} MB (keep under ${MAX_BYTES / (1024 * 1024)} MB)`);
  }
  return issues;
}

// Resolution comes from the original upload when storage has it (migrated pictures only
// have medium); file size comes from medium, which is what players download.
function checkPicture(picture) {
  if (!qualityCache.has(picture.id)) {
    const dimensions = (picture.original_url ? loadDimensions(picture.original_url) : Promise.reject())
      .catch(() => loadDimensions(picture.url))
      .catch(() => null);
    const bytes = fileBytes(picture.url).catch(() => null);
    qualityCache.set(
      picture.id,
      Promise.all([dimensions, bytes]).then(([d, b]) => pictureIssues(d, b))
    );
  }
  return qualityCache.get(picture.id);
}

export default function PicturesSection({ api, detail, practicePictureIds = [], onDetailChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [errors, setErrors] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [issuesById, setIssuesById] = useState({});
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);
  const uploadLock = useRef(false);

  useEffect(() => {
    let cancelled = false;
    detail.pictures.forEach((p) => {
      checkPicture(p).then((issues) => {
        if (!cancelled) setIssuesById((prev) => ({ ...prev, [p.id]: issues }));
      });
    });
    return () => { cancelled = true; };
  }, [detail.pictures]);

  const flagged = detail.pictures
    .map((p, i) => ({ picture: p, index: i, issues: issuesById[p.id] || [] }))
    .filter((f) => f.issues.length > 0);
  function practiceSlot(pictureId) {
    return practicePictureIds.findIndex((id) => id != null && id === pictureId);
  }

  const premiumPractice = detail.pictures
    .map((p, i) => ({ picture: p, index: i, slot: practiceSlot(p.id) }))
    .filter(({ picture, slot }) => picture.premium && slot >= 0);
  const basicCount = detail.pictures.filter((p) => !p.premium).length;
  const extendedCount = detail.pictures.length - basicCount;

  async function upload(fileList) {
    const incoming = Array.from(fileList || []);
    const files = incoming.filter(isUploadableImage);
    const skipped = incoming.length - files.length;
    if (!files.length || uploadLock.current) {
      if (incoming.length && !files.length) {
        setErrors(['Those files aren’t PNG, JPG, or GIF images.']);
      }
      return;
    }
    uploadLock.current = true;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    setErrors(skipped ? [`Skipped ${skipped} ${skipped === 1 ? 'file that isn’t' : 'files that aren’t'} a PNG, JPG, or GIF.`] : []);
    const batchErrors = [];
    try {
      for (let i = 0; i < files.length; i += UPLOAD_BATCH) {
        const batch = files.slice(i, i + UPLOAD_BATCH);
        try {
          const next = await api.addPictures(detail.id, batch);
          if (next.upload_errors) batchErrors.push(...next.upload_errors);
          onDetailChange(next);
        } catch (e) {
          batchErrors.push(batch.length > 1 ? `${batch.length} pictures: ${e.message}` : e.message);
        }
        setUploadProgress({ done: Math.min(i + batch.length, files.length), total: files.length });
      }
      if (batchErrors.length) {
        setErrors((prev) => [...prev, ...batchErrors]);
      }
    } finally {
      uploadLock.current = false;
      setUploading(false);
      setUploadProgress(null);
    }
  }

  async function setPremium(picture, premium) {
    if (picture.premium === premium) return;
    setDeletingId(picture.id);
    setErrors([]);
    try {
      onDetailChange(await api.updatePicture(detail.id, picture.id, { premium }));
    } catch (e) {
      setErrors([e.message]);
    } finally {
      setDeletingId(null);
    }
  }

  async function remove(picture, index) {
    const slot = practiceSlot(picture.id);
    const practiceNote = slot >= 0
      ? ` It is the picture for practice round ${slot + 1}.`
      : '';
    if (!window.confirm(`Delete picture ${index + 1}?${practiceNote}`)) return;
    setDeletingId(picture.id);
    setErrors([]);
    try {
      onDetailChange(await api.deletePicture(detail.id, picture.id));
    } catch (e) {
      setErrors([e.message]);
    } finally {
      setDeletingId(null);
    }
  }

  function onDragEnter(e) {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setDragOver(true);
  }

  function onDragOver(e) {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragOver(false);
    upload(filesFromDataTransfer(e.dataTransfer));
  }

  function openPicker() {
    if (uploadLock.current || !fileInputRef.current) return;
    fileInputRef.current.click();
  }

  const uploadLabel = uploading ? 'Uploading…' : 'Add pictures';
  const uploadHint = uploading && uploadProgress && uploadProgress.total > 1
    ? `${uploadProgress.done} of ${uploadProgress.total}`
    : 'Drop or click · several at once';

  return (
    <div
      className={`aaPictures${dragOver ? ' aaPictures--over' : ''}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <h4 className="aaSubheading">Pictures <span className="aaSubheading__count">{detail.pictures.length}</span></h4>
      <p className="aaField__hint">
        Basic pack is the unmarked pictures ({basicCount}). Extended pack is all {detail.pictures.length} of them
        {extendedCount > 0 ? `, including ${extendedCount} premium` : ''}. Tap a picture’s label to move it.
        Each round still shows up to {detail.board_size}. Drop several images on this section to add them all. Changes save straight away.
      </p>

      {errors.length > 0 && (
        <ul className="aaErrorList">
          {errors.map((err) => <li key={err} className="aaError">{err}</li>)}
        </ul>
      )}

      <div className="aaPictures__grid">
        {detail.pictures.map((p, i) => {
          const issues = issuesById[p.id] || [];
          const busy = deletingId === p.id;
          return (
            <figure
              key={p.id}
              className={`aaPicture${busy ? ' aaPicture--busy' : ''}${issues.length ? ' aaPicture--flagged' : ''}${p.premium ? ' aaPicture--premium' : ''}`}
            >
              <div className="aaPicture__frame">
                <a href={p.url} target="_blank" rel="noreferrer" title={p.file_name}>
                  <img src={p.url} alt={p.file_name || `Picture ${i + 1}`} loading="lazy" />
                </a>
                <span className="aaPicture__number">{i + 1}</span>
                {practiceSlot(p.id) >= 0 && <span className="aaPicture__practice">Practice {practiceSlot(p.id) + 1}</span>}
                {issues.length > 0 && (
                  <span className="aaPicture__alert" title={issues.join('\n')} aria-label={issues.join('. ')}>⚠</span>
                )}
                <button
                  type="button"
                  className="aaPicture__delete"
                  onClick={() => remove(p, i)}
                  disabled={deletingId != null}
                  aria-label={`Delete picture ${i + 1}`}
                >
                  ✕
                </button>
              </div>
              <button
                type="button"
                className={`aaPicture__tier${p.premium ? ' aaPicture__tier--premium' : ''}`}
                onClick={() => setPremium(p, !p.premium)}
                disabled={deletingId != null}
                aria-pressed={!!p.premium}
                aria-label={p.premium
                  ? `Picture ${i + 1} is in Extended pack. Move it to Basic pack.`
                  : `Picture ${i + 1} is in Basic pack. Move it to Extended pack.`}
              >
                {p.premium ? '★ Extended' : 'Basic'}
              </button>
            </figure>
          );
        })}

        <div
          className={`aaDrop${dragOver ? ' aaDrop--over' : ''}${uploading ? ' aaDrop--busy' : ''}`}
          role="button"
          tabIndex={0}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
        >
          <span className="aaDrop__icon" aria-hidden="true">{uploading ? '⏳' : '＋'}</span>
          <span>{uploadLabel}</span>
          <span className="aaDrop__hint">{uploadHint}</span>
        </div>
      </div>
      <input
        ref={fileInputRef}
        className="aaDrop__input"
        type="file"
        accept={ACCEPT}
        multiple
        disabled={uploading}
        onChange={(e) => { upload(e.target.files); e.target.value = ''; }}
      />

      {premiumPractice.length > 0 && (
        <div className="aaPictureAlerts">
          <p className="aaPictureAlerts__title">Practice pictures should stay in the basic pack</p>
          <ul className="aaPictureAlerts__list">
            {premiumPractice.map(({ index, slot }) => (
              <li key={index}>
                Picture {index + 1} is practice round {slot + 1}. The practice prompt describes it, so leave it as Basic.
              </li>
            ))}
          </ul>
        </div>
      )}

      {flagged.length > 0 && (
        <div className="aaPictureAlerts">
          <p className="aaPictureAlerts__title">
            ⚠ {flagged.length} {flagged.length === 1 ? 'picture needs' : 'pictures need'} re-uploading
          </p>
          <ul className="aaPictureAlerts__list">
            {flagged.map(({ picture, index, issues }) => (
              <li key={picture.id}>
                <strong>Picture {index + 1}</strong> <span className="aaPictureAlerts__file">{picture.file_name}</span>
                {' · '}{issues.join(' · ')}
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  );
}
